import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-overview';

/**
 * Overview text block for introductory or explanatory content (e.g. "What is {keyword}").
 * Single 8-column container with a heading stacked above a paragraph of body text.
 * Minimal text-only layout; pick richer SignNow types when imagery, lists, or cards are needed.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for {keyword} overview. 6-10 words.
 * @slot text - {content} <me-text> - Introductory paragraph explaining what {keyword} is and how it works. Plain-language definition and core mechanics. You MUST write at least 80 words.
 * @example
 *   <sn-overview>
 *     <me-heading slot="heading" variant="4" tag="h2">What is e-signature</me-heading>
 *     <me-text slot="text">An electronic signature is a digital form of a handwritten signature that is legally binding.</me-text>
 *   </sn-overview>
 */
@Component({
  tag: 'sn-overview',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcTextBlock {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Legacy / raw-HTML fallback. For new usage prefer named slots.
   * @dynamic
   * @type {object}
   * @property {string} title - Section heading text (use slot="heading" instead)
   * @property {string} text - Body paragraph text (use slot="text" instead)
   * @property {string} raw_html - Raw HTML content (bypasses innerHTML parsing)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
  }

  render() {
    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column">
          <me-column spanLg="8">
            <slot name="heading"></slot>
            <slot name="text"></slot>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
